import type { Ref } from 'react'
import { IconCartGlyph, IconPaperPlane, IconWarningTriangle } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { RepriceNote } from '../../components/ui/RepriceNote'
import { Spinner } from '../../components/ui/Spinner'
import { ShareGlyph } from '../../components/ui/lucide-glyphs'
import type { components } from '../../lib/api/schema.gen'
import { PRICING_RFQ_COMPOSE_NOTE } from '../../lib/pricing-copy'
import { formatMoney } from '../cart/cart-money'
import type { AddressView, ShippingAccountView } from '../checkout/checkout-domain'
import { ShipToAddressField, ShippingMethodFields } from './ShippingCard'
import { rfqSubmitBlockers } from './review-domain'
import type { RequestSummary, RfqSubmitBlocker } from './review-domain'

type RfqShipMethod = components['schemas']['RfqShipMethod']
type RfqCostEstimate = components['schemas']['RfqCostEstimate']

export interface RequestSummaryRailProps {
  readonly summary: RequestSummary
  /** UID-333 D2 (ADR-0116) — the ship-to and ship-method pickers, MOVED here
   * from `RequestDetailsSection`. Their behaviour, validation and wire mapping
   * are ADR-0053/ADR-0076's and are untouched; only the mount point changed.
   * They were previously inside a default-COLLAPSED section, so neither was
   * visible on first load — which is most of why the built rail was so much
   * shorter than the design's (`dc.html:4341-4356` puts both in the rail).
   * Moved, never duplicated: two live copies of one controlled `<select>` over
   * a single piece of draft state would mean divergent validation targets and
   * two `aria-describedby` anchors for one error. */
  readonly shipMethod: RfqShipMethod | 'none'
  readonly onShipMethodChange: (value: RfqShipMethod | 'none') => void
  readonly shippingAccounts: readonly ShippingAccountView[]
  readonly selectedShippingAccountId: string | null
  readonly onSelectShippingAccount: (id: string) => void
  readonly onAddShippingAccount: () => void
  readonly shipAccountError?: string | undefined
  readonly shipAccountErrorRef?: Ref<HTMLParagraphElement>
  readonly addresses: readonly AddressView[]
  readonly shipToAddressId: string
  readonly onShipToAddressIdChange: (value: string) => void
  readonly onAddNewAddress: () => void
  readonly submitting: boolean
  readonly onSubmit: () => void
  /** UID-586 — true once `handleSubmit` has already succeeded for this
   * request and it is still awaiting a quote (not yet `quoted` — once it
   * is, `RfqReviewPage` swaps this whole rail for `RfqDetailSummaryRail`
   * instead). Replaces the primary CTA with a disabled "Quote in
   * progress..." button (confirmed against a real design screenshot),
   * rather than hiding or disabling the rest of this rail — Ship
   * to/method stay interactive per the banner's own "keep editing" copy. */
  readonly pending?: boolean
  /** UID-242 Item 1 (ADR-0082) — enabled only once the compose screen's eagerly-created
   * server draft (`RfqBundleValue.draftRfqId`) exists; there is no id to share before
   * that. See `onShare`'s doc comment for the MVP scope. */
  readonly shareEnabled: boolean
  /** True while the share-time `PATCH /rfq/{rfqId}` sync is in flight. */
  readonly sharing: boolean
  /** UID-242 Item 1 (ADR-0082), amended by UID-466 (ADR-0152) — "Share for review".
   * Clicking syncs the server draft (`PATCH /rfq/{rfqId}`) and then OPENS the design's
   * "Share · this RFQ" dialog (`RfqShareModal`, `dc.html:4824-4854`), which is where
   * the copy-link action now lives. ADR-0082's copy-link behavior itself is unchanged
   * — the internal, tenant-scoped `${origin}/rfq/{draftRfqId}` deep link, same
   * copy-then-toast UX as the quoted detail page's share action
   * (`RfqShareQuoteModal.handleCopyLink` — that UX moved there from
   * `ShareHandoffModal`, retired by UID-438/ADR-0143 D1). Still deliberately NOT the
   * external `SharedSession`/`shareUrl` mechanism (that's minted later, at
   * `mark-ready`, on the quoted detail page).
   *
   * ADR-0082 §6 rejected a people-picker outright because no teammate-directory API
   * exists to build one against. That is STILL TRUE — the dialog's roster is a
   * hardcoded frontend stub and its "Add N person(s)" action grants nothing. ADR-0152
   * reverses §6 only for the SHELL, and records both stubs; see `RfqShareModal` and
   * `share-people.ts`. This rail is unaffected either way: it fires `onShare` and
   * owns none of that. */
  readonly onShare: () => void
  /** UID-260 (ADR-0085, design fact #4) — how many lines still lack a
   * `specSelection`. While this is non-zero the CTA keeps its normal
   * "Submit RFQ · N parts" label but is DISABLED with a tooltip — this
   * mirrors the `.dc.html` prototype's actual JS logic exactly
   * (`submitDisabled: needsConfigCount > 0`, grey `--bg-1`/`--border-2`
   * styling, `cursor: not-allowed`, a tooltip reading "{n} part(s) still
   * need(s) to be configured before you can request a quote."), per the
   * standing rule that `.dc.html`'s logic is authoritative over README
   * prose when the two disagree (this ticket's README §8 wording — "the
   * bottom CTA becomes Configure" — was the earlier, now-superseded
   * reading; corrected 2026-08-24). Per-row/bulk "Configure" actions on
   * `PartsReviewTable` remain the only way to actually open the Configure
   * workflow. */
  readonly needsConfigCount: number
  /** UID-263 (ADR-0087 D1/D4) — the cart-eligible count (`lines.filter(isCatalogLine).length`,
   * NEVER the header `pricedCount`) and the visibility flag the ticket's
   * design source computes from it (`showAddBulk = cartEligibleCount > 0 &&
   * !allCartEligible`, `dc.html:9730`). Both are owned/computed by
   * `RfqReviewPage` (which owns `lines`) and passed down as plain props —
   * this component renders, it does not derive the set. */
  readonly cartEligibleCount: number
  readonly showAddBulk: boolean
  /** UID-309 Gate 5 code review (S4) — the "Part production (N parts)" label's
   * count: the EXACT set `review-domain.partProductionSubtotal` sums
   * (`partProductionLineCount`), never `cartEligibleCount` above.
   * `cartEligibleCount` is every catalog line (`isCatalogLine`, no price
   * requirement) — right for the "Add N priced to cart" button below, but
   * wrong for this label, since an unpriced (or malformed-price) catalog
   * line contributes nothing to the subtotal yet would still be counted,
   * producing a label/total mismatch (e.g. "(2 parts) — $10.00" from only
   * one priced line). */
  readonly partProductionCount: number
  /** UID-333 F5 (ADR-0116) — how many lines still need a real quote, behind the
   * rail's lavender "Custom quote  N part(s)" row.
   *
   * A PROP, computed by `review-domain.needsQuoteLineCount`, not derived here
   * as `partsCount - partProductionCount`: that subtraction over-counted, since
   * `partProductionCount` is priced-CATALOG-lines-only and its complement swept
   * in unpriced catalog lines and auto-priced custom candidates. See that
   * helper's doc comment. */
  readonly customQuoteCount: number
  /** UID-309 (reference parity) — the real, sum-of-catalog-line-prices
   * subtotal (`review-domain.partProductionSubtotal`) — NEVER includes
   * custom lines, which have no committed price pre-quote (ADR-0085 D1).
   *
   * UID-401 (ADR-0125 D5, superseding ADR-0118 D2 / ADR-0116 D3 / ADR-0100
   * D1) — Shipping, Tax and Total are now REAL, SERVER-DERIVED figures (see
   * the `costEstimate` prop below), not the always-`N/A` state this comment
   * used to describe. This prop remains the Part-production row's own
   * figure and is UNCHANGED — it is `costEstimate.partProduction` that feeds
   * Shipping/Tax/Total's addends, never a client-side sum of this value.
   *
   * `null` means the subtotal is NOT COMPUTABLE — the priced catalog lines carry
   * mixed currencies, so there is no single honest figure to show (see
   * `partProductionSubtotal`'s doc comment). Rendered as `N/A`, matching the
   * established treatment of the sibling `subtotalForQuote`'s `null` at
   * `OpenQuotes.tsx:251`. Note `0.00` and `null` are DIFFERENT states here:
   * `0.00` = "nothing priced yet", `null` = "cannot be summed". */
  readonly partProduction: { readonly amount: string; readonly currency: string } | null
  /** UID-401 (ADR-0125 D5) — the SERVER-DERIVED shipping/tax/total estimate
   * (`RfqView.costEstimate`, ALWAYS emitted by the server), owned and synced
   * by `RfqReviewPage` from the latest `POST /rfq` / `PATCH /rfq/{rfqId}`
   * response. This is the ONLY source this component ever reads for the
   * Shipping/Tax/Total rows — no figure on this rail is computed, summed, or
   * guessed client-side. `undefined` only while the very first server round
   * trip is still in flight (before any draft exists); every row honestly
   * falls back to the absent-value glyph in that window, never a fabricated
   * placeholder. */
  readonly costEstimate?: RfqCostEstimate | undefined
  /** True while the bulk batch (`Promise.allSettled` over N `POST /cart/lines`,
   * ADR-0087 D3) is in flight — disables the button and swaps its label to
   * "Adding to cart…" (`aria-busy`, UX spec §2 state 2). */
  readonly bulkAdding: boolean
  /** UID-263 — opens the confirm modal (or, if `ff_hide_bulk_cart_warn`
   * suppresses it, runs the batch add directly) — the decision lives in
   * `RfqReviewPage`, not here. */
  readonly onAddBulk: () => void
  /** UID-401 TASK D — `true` once every line is priced
   * (`review-domain.allLinesPriced`, TASK A), mirroring the design's
   * `quoteView`/`allPriced` gate (`dc.html:4357`). Owned by `RfqReviewPage`
   * (which owns `lines`); this component only renders the CTA the flag
   * says to. */
  readonly showCheckout: boolean
  /** UID-401 TASK D (this component) — click hook only. `RfqReviewPage` owns
   * WHAT clicking Checkout does: it now opens the shared
   * `BulkAddPricedConfirmModal` (UID-401 Part 2 — reusing the same
   * cart-eligible-lines-removal executor, `runBulkAdd`/
   * `removeLinesFromRfqAndMaybeDiscard`, ADR-0087 D2, as the existing bulk
   * "Add N priced to cart" button) rather than navigating straight to
   * `/cart` the way this comment used to describe — leaving on Checkout
   * without moving the priced lines into the cart would drop the user on an
   * empty cart. See `RfqReviewPage.handleCheckoutClick`. Cart page behavior
   * itself is still UID-403's scope, not this component's. */
  readonly onCheckout: () => void
  /** UID-401 (this ticket, Part 1/Part 2) — click hook for the rail's
   * secondary button once it has swapped to "Generate quote" (see that
   * button's own doc comment below for the full label-swap contract). Same
   * shape as `onCheckout` above and, in `RfqReviewPage`, funnels through the
   * SAME confirm-modal + bulk-add executor — the only difference is the
   * one-shot cart-arrival intent `RfqReviewPage` attaches to the resulting
   * `/cart` navigation (UID-401 Part 3; consumed by UID-403, not built
   * here). */
  readonly onGenerateQuote: () => void
}

const BLOCKER_HINT: Record<RfqSubmitBlocker, string> = {
  no_parts: 'Add at least one part before submitting.',
}

/**
 * RFQ review screen's sticky request-summary rail (screens.md §15 —
 * "Order-summary-style rail"): parts/units/services counts, the
 * pricing-turnaround callout, and the "Submit RFQ package" CTA. Per current
 * product direction (superseding the earlier `reqComplete`-derived reading
 * of Fictiv Platform.dc.html L7873 that used to gate this on a need-by date
 * and a services decision too), need-by date and requested services are
 * OPTIONAL, informational-only fields — the ONLY submit blockers are
 * "at least one part" (`rfqSubmitBlockers`) and part-configuration
 * completeness (`needsConfigCount`).
 *
 * UID-242 Item 1 (ADR-0082) — supersedes the earlier scope-cut note that lived here: the
 * design reference (Fictiv Platform.dc.html L4052, `rfqDraftView`) shows a persistent
 * "Share for review" secondary button alongside Submit. That premise ("there is no RFQ
 * id at compose time") is removed by ADR-0082's eager-draft-creation change — the
 * compose screen now mints a real server draft on the 0→1 parts transition
 * (`RfqReviewPage`'s effect), so a real `rfqId` exists to share well before Submit. This
 * button is enabled only once that id exists (`shareEnabled`) and copies the INTERNAL
 * `/rfq/{rfqId}` deep link — see `onShare`'s doc comment for the copy-link-only MVP
 * scope. This is deliberately NOT the external `SharedSession`/`shareUrl` mechanism,
 * which is still minted later at `mark-ready` on the quoted detail page
 * (`RfqCard.tsx`'s `useShareHandoff` / `RfqShareQuoteModal.tsx`) — that flow is
 * unaffected by this ticket.
 */
export function RequestSummaryRail({
  summary,
  submitting,
  onSubmit,
  pending = false,
  shareEnabled,
  sharing,
  onShare,
  needsConfigCount,
  cartEligibleCount,
  showAddBulk,
  bulkAdding,
  onAddBulk,
  showCheckout,
  onCheckout,
  onGenerateQuote,
  partProduction,
  costEstimate,
  partProductionCount,
  customQuoteCount,
  shipMethod,
  onShipMethodChange,
  shippingAccounts,
  selectedShippingAccountId,
  onSelectShippingAccount,
  onAddShippingAccount,
  shipAccountError,
  shipAccountErrorRef,
  addresses,
  shipToAddressId,
  onShipToAddressIdChange,
  onAddNewAddress,
}: RequestSummaryRailProps) {
  const blockers = rfqSubmitBlockers({ partsCount: summary.partsCount })
  const needsConfigure = needsConfigCount > 0
  const canSubmit = blockers.length === 0 && !submitting && !needsConfigure

  // The ONLY remaining submit blocker (besides "at least one part") is
  // part-configuration completeness. This exact string is load-bearing —
  // it is the ONLY warning the rail ever shows, and only while
  // `needsConfigCount > 0`; once every part is configured (and at least one
  // part exists) no banner renders at all.
  const configGateMessage = needsConfigure
    ? `${needsConfigCount} part${needsConfigCount === 1 ? '' : 's'} still need${
        needsConfigCount === 1 ? 's' : ''
      } to be configured before you can request a quote.`
    : undefined
  /** Every reason Submit is disabled, config gate first, folded into the
   * CTA's `title` tooltip. The config-gate `<output>` below shows ONLY
   * `configGateMessage` — the `no_parts` hint is tooltip-only, since an
   * empty RFQ is already self-evident from the disabled CTA and doesn't need
   * its own banner. */
  const submitBlockerMessages: readonly string[] = [
    configGateMessage,
    ...blockers.map((blocker) => BLOCKER_HINT[blocker]),
  ].filter((hint): hint is string => hint !== undefined)
  const submitTooltip = submitBlockerMessages.join(' ') || undefined

  const partProductionDisplay = partProduction
    ? formatMoney(partProduction.amount, partProduction.currency)
    : 'N/A'

  // UID-401 (ADR-0125 D5) — Shipping/Tax/Total are now rendered EXCLUSIVELY
  // from the server's `costEstimate`, keyed on its own discriminators
  // (`shippingBasis`/`taxBasis`) rather than guessed from `shipMethod`/
  // `shipToAddressId` locally. `formatMoney` only FORMATS a string the
  // server already computed — it is display formatting, not derivation
  // (the same helper `partProductionDisplay` above already uses on a
  // server-derived figure). Nothing here sums, rounds, or rates anything.
  //
  // `own_account` renders the rate card's own `shippingNote` ("Billed by
  // carrier…"), NEVER a figure — a real fabrication risk this app must not
  // take (ADR-0125 D5 #3). Every other non-`rate_card` basis (no ship
  // method chosen yet, or nothing on the RFQ is priced) is the honest
  // absent-value glyph, never `0.00` (ADR-0100 D1).
  const shippingDisplay = (() => {
    if (!costEstimate) return 'N/A'
    if (costEstimate.shippingBasis === 'rate_card' && costEstimate.shipping) {
      return formatMoney(costEstimate.shipping.amount, costEstimate.shipping.currency)
    }
    if (costEstimate.shippingBasis === 'billed_by_carrier') {
      return costEstimate.shippingNote ?? 'Billed by carrier'
    }
    return 'N/A'
  })()

  // Tax renders a real figure ONLY on `destination_rate` (a resolved
  // ship-to address's `salesTaxRatePct`) — including a genuine `0.00` for a
  // tax-free destination, which is a real, derived answer and categorically
  // different from "no honest figure" (ADR-0125 D5).
  const taxDisplay = (() => {
    if (!costEstimate) return 'N/A'
    if (costEstimate.taxBasis === 'destination_rate' && costEstimate.tax) {
      return formatMoney(costEstimate.tax.amount, costEstimate.tax.currency)
    }
    return 'N/A'
  })()

  // Total is EXACTLY `costEstimate.total` — the sum of precisely the
  // addends the server names in `totalIncludes`. Never client-summed from
  // `partProduction`/`shippingDisplay`/`taxDisplay`, so no addend (real or
  // fabricated) can be smuggled in by a client-side `+`.
  const totalDisplay =
    costEstimate?.total !== null && costEstimate?.total !== undefined
      ? formatMoney(costEstimate.total.amount, costEstimate.total.currency)
      : 'N/A'

  return (
    <Card aria-labelledby="rfq-summary-heading" className="rfq-summary-rail">
      <h2 id="rfq-summary-heading" className="heading-text">
        Request summary
      </h2>
      {/* UID-333 F5 (ADR-0116) — the design uses summary ROWS
       * (`dc.html:4333-4337`), not three big side-by-side stat tiles: a single
       * `Parts  N · Q units` row, then rows that appear only when they have
       * something to say. */}
      <dl className="rfq-summary-rows">
        <div className="rfq-summary-row">
          <dt>Parts</dt>
          <dd>
            {summary.partsCount} · {summary.unitsCount} units
          </dd>
        </div>

        {/* Teal `Priced` row — the parts that DO contribute a real subtotal. */}
        {partProductionCount > 0 && (
          <div className="rfq-summary-row rfq-summary-row-priced">
            <dt>Priced</dt>
            <dd>
              {partProductionCount} · {partProductionDisplay}
            </dd>
          </div>
        )}

        {/* Lavender `Custom quote  N part(s)` row — the design's PRIMARY
         * "these parts need quoting" signal, which had no equivalent anywhere
         * in the build. Derived as the parts that contribute nothing to the
         * priced subtotal, which is exactly what "needs a quote" means here:
         * `partProductionCount` is the precise set
         * `partProductionSubtotal` sums (see its prop doc above), so the
         * remainder is honest rather than a second, differently-counted
         * notion of "unpriced". */}
        {customQuoteCount > 0 && (
          <div className="rfq-summary-row rfq-summary-row-custom-quote">
            <dt>Custom quote</dt>
            <dd>
              {customQuoteCount} {customQuoteCount === 1 ? 'part' : 'parts'}
            </dd>
          </div>
        )}

        {summary.servicesCount > 0 && (
          <div className="rfq-summary-row rfq-summary-row-services">
            <dt>Add-on services</dt>
            <dd>{summary.servicesCount}</dd>
          </div>
        )}
      </dl>

      {/* UID-333 F9 / UID-359 item 13 (ADR-0116 F9, ADR-0118 D2 target row 3)
       * — the config-gate callout MOVES here, directly under the summary rows
       * and above the divider, matching the design's `showConfigGate`
       * position (`dc.html:4336`, right after Custom-quote/before Add-on
       * services — this build's rows/services boundary is close enough that
       * "right after the rows block" is the faithful mount point; it no
       * longer sits below Submit). Still ONE `<output>` (implicit
       * `role="status"`) for the whole rail — see its contents' own doc
       * comment further down for why `<output>` and why every blocker lives
       * here rather than only in the disabled Submit button's `title`. */}
      {configGateMessage && (
        <output className="rfq-summary-config-gate">
          <IconWarningTriangle size={14} />
          <span>
            <span className="rfq-summary-config-gate-line">{configGateMessage}</span>
          </span>
        </output>
      )}

      {/* `dc.html:4339` — a plain hairline divider between the rows/callout
       * block and the "Ship to" section. */}
      <div className="rfq-summary-divider" />

      {/* UID-333 D2 (ADR-0116) / UID-359 item 13 (ADR-0118 D2, target row 5)
       * — the design's "Ship to" block (`dc.html:4340-4344`): a saved-address
       * select plus a resolved address line. MOVED from `RequestDetailsSection`
       * originally (ADR-0116), and now mounted ABOVE Part production per the
       * design's row order, split out of the combined `ShippingCard` (see
       * `ShipToAddressField`'s doc comment for why the shipping-METHOD select
       * is NOT adjacent to this one — the design itself separates them). The
       * state itself still lives in `RfqReviewPage`; this is a mount-point
       * change, not a state migration.
       *
       * UID-359 item 13 correction (2026-08-27) — restores the design's own
       * visible caption above the select (`dc.html:4341`, literal string
       * "Ship to"). An earlier brief said "drop the visible 'Shipping
       * method' / 'Ship-to address' field labels" and that was read as
       * dropping this caption too — it shouldn't have been: that instruction
       * was about the OLD build's field labels (now correctly gone, their
       * accessible names living on `aria-label`), not about this design
       * caption, which is a different piece of copy. Rendered as a plain,
       * non-labelling `<span>` (no `htmlFor`/`id` wiring to the select) so it
       * cannot create a second, competing accessible name against the
       * select's existing `aria-label="Ship-to address"` — which stays
       * exactly as-is, still pinned by `ShippingCard.test.tsx` and ~10
       * `RfqReviewPage.test.tsx` `getByLabelText` sites this change does not
       * touch. See `RequestSummaryRail.test.tsx` for the test pinning both
       * halves (visible caption text AND the select's accessible name) so
       * this can't regress in either direction again. */}
      <span className="rfq-summary-shipto-caption">Ship to</span>
      <ShipToAddressField
        addresses={addresses}
        shipToAddressId={shipToAddressId}
        onShipToAddressIdChange={onShipToAddressIdChange}
        onAddNewAddress={onAddNewAddress}
      />

      {/* UID-401 (ADR-0125 D5) — Part production and Shipping are both REAL
       * figures now: Part production from the `partProduction` prop (the
       * priced-catalog-lines subtotal, unchanged), Shipping from the
       * server's `costEstimate` — the rate card keyed on the persisted
       * `shipMethod`, never a client-side guess. */}
      <dl className="rfq-summary-cost-breakdown">
        <div className="rfq-summary-cost-line">
          <dt>Part production ({partProductionCount} parts)</dt>
          <dd>{partProductionDisplay}</dd>
        </div>
        {/* UID-401 (ADR-0125 D5, target row 7) — `dc.html:4347`'s qualifier
         * is dynamic (`shipNote`: "estimate" or "billed by carrier"); this
         * build keeps a static "(estimate)" qualifier (this is a
         * pre-checkout estimate either way, ADR-0061) and lets `<dd>` itself
         * carry the `own_account` "Billed by carrier…" note instead. The
         * value is the server's `shipping` figure on `rate_card`, the rate
         * card's own `shippingNote` on `billed_by_carrier` (never a figure —
         * D5 #3), or the absent-value glyph `N/A` otherwise (no ship method
         * chosen yet, or nothing on the RFQ is priced) — never `0.00`
         * (ADR-0100 D1) and never computed in the browser. */}
        <div className="rfq-summary-cost-line rfq-summary-cost-line-shipping">
          <dt>
            Shipping <span className="rfq-summary-cost-qualifier">(estimate)</span>
          </dt>
          <dd>{shippingDisplay}</dd>
        </div>
      </dl>

      {/* `dc.html:4348-4351` — the shipping-method select (+ conditional
       * own-account picker) mounts immediately below the Shipping row, NOT
       * grouped with "Ship to" above — see `ShippingMethodFields`'s doc
       * comment. */}
      <ShippingMethodFields
        shipMethod={shipMethod}
        onShipMethodChange={onShipMethodChange}
        shippingAccounts={shippingAccounts}
        selectedShippingAccountId={selectedShippingAccountId}
        onSelectShippingAccount={onSelectShippingAccount}
        onAddShippingAccount={onAddShippingAccount}
        shipAccountError={shipAccountError}
        shipAccountErrorRef={shipAccountErrorRef}
      />

      {/* UID-401 (ADR-0125 D5, target row 8) — Tax, same source discipline as
       * Shipping: a real figure ONLY on `destination_rate` (the resolved
       * ship-to address's own `salesTaxRatePct`, echoed by the server),
       * otherwise the absent-value glyph — never fabricated, never a client
       * rate. */}
      <dl className="rfq-summary-cost-breakdown">
        <div className="rfq-summary-cost-line">
          <dt>Tax</dt>
          <dd>{taxDisplay}</dd>
        </div>
      </dl>

      {/* UID-359 item 13 (ADR-0118 D2.1 #4 / D2.2) — the disclosure that
       * Shipping/Tax are absent-valued (always true at compose stage) is a
       * STATIC note, not a second live region — the rail's only live region
       * stays the config-gate `<output>` above. Reuses the house
       * `RepriceNote` mechanism rather than an inline qualifier on "Total"
       * (D2.2 — "Total" stays bare, matching every other rail in this app). */}
      <RepriceNote note={PRICING_RFQ_COMPOSE_NOTE} />

      {/* `dc.html:4354` — a plain hairline divider directly above Total. */}
      <div className="rfq-summary-divider rfq-summary-divider-total" />

      {/* UID-401 (ADR-0125 D5, target row 10) — Total is EXACTLY
       * `costEstimate.total`, the server's own sum of precisely the addends
       * named in `costEstimate.totalIncludes` — never a client-side
       * `partProduction + shipping + tax`, which could silently disagree
       * with what the server actually summed (e.g. `own_account` shipping,
       * which contributes no addend at all). */}
      <dl className="rfq-summary-cost-breakdown rfq-summary-total-row">
        <div className="rfq-summary-cost-line rfq-summary-cost-line-total">
          <dt>Total</dt>
          <dd>{totalDisplay}</dd>
        </div>
      </dl>

      {/* UID-401 TASK D — ticket bug: '"Submit RFQ" stays disabled even after
       * configuring — should allow going straight to checkout at that
       * point.' The design already answers this (`dc.html:4357-4358`): once
       * `quoteView`/`allPriced`, the rail renders a `Checkout` CTA ABOVE
       * Submit RFQ, `height:46px; background:var(--fictiv-teal); color:#fff;
       * font-size:14px; font-weight:700; margin-bottom:9px; width:100%;
       * border-radius:4px`. `var(--fictiv-teal)` in this design source maps
       * to this app's `--color-brand-primary` (navy, see this file's own
       * `.rfq-summary-cta` neighbor comment / rfq.css's established
       * substitution) — exactly `variant="primary"`'s existing fill
       * (`ui.css` `.btn-primary`), so no new token or variant is needed. */}
      {showCheckout && (
        <Button
          variant="primary"
          size="lg"
          className="rfq-summary-checkout-cta"
          onClick={onCheckout}
        >
          <IconCartGlyph size={17} />
          Checkout
        </Button>
      )}
      {/* UID-260 (ADR-0085, design fact #4) — the CTA keeps ONE label always
       * ("Submit RFQ · N parts") and is disabled+tooltipped while any line
       * needs Configure, exactly matching `.dc.html`'s actual
       * `submitBtnStyle`/`submitDisabled` logic (design source of truth per
       * the standing rule — a prior reading of README §8's prose that had
       * this CTA relabel to an active "Configure" control was wrong and has
       * been reverted, 2026-08-24).
       *
       * UID-359 item 13 (ADR-0118 D4) — the design's literal fill for this
       * button is `#8b5cf6`, which measures ~4.24:1 for white 14px/700 label
       * text and FAILS WCAG AA's 4.5:1 floor (14px bold does not qualify as
       * "large text"). `variant="purple"` today resolves to
       * `--color-accent-purple` (#9747ff, ~4.51:1 — a different hue family
       * entirely, and only barely passing). Per ADR-0118 D4, the CSS for
       * `.rfq-summary-cta` must instead paint this button's SOLID FILL with
       * `--color-accent-violet-text` (#7C3AED, ~5.70:1 on white) — the exact
       * precedent already recorded for this same "Send to RFQ"-shaped CTA at
       * `tokens.css:113-120` (UID-225). Same violet hue family as the
       * design's intent, one shade darker, clears AA, introduces no new
       * token. This is a deliberate, reported one-hex non-pixel-match, not an
       * oversight — see this file's accompanying report for the exact CSS
       * change needed (out of this component's ownership). */}
      {/* UID-586 (`Fictiv Platform.dc.html:4362`) — confirmed against a real
       * design screenshot: once submitted but still awaiting a quote, this
       * CTA slot shows a disabled, neutral "Quote in progress…" button
       * (spinner + text) INSTEAD of Submit, rather than a disabled Submit
       * button relabelled — the design never keeps the purple "Submit RFQ"
       * label/icon visible once the request has actually been sent. */}
      {pending ? (
        <button type="button" className="rfq-summary-quote-in-progress-cta" disabled>
          <Spinner size="sm" aria-hidden="true" className="rfq-summary-quote-in-progress-spinner" />
          Quote in progress…
        </button>
      ) : (
        <Button
          variant="purple"
          size="lg"
          className="rfq-summary-cta"
          disabled={!canSubmit}
          loading={submitting}
          onClick={onSubmit}
          /* UID-333 F9 (ADR-0116) — the design surfaces every submit blocker
           * through the disabled button's own tooltip (`submitTooltip`,
           * `:9736`), not as loose sentences around Share. `submitTooltip`
           * folds in the `rfqSubmitBlockers` reasons too, so nothing that used
           * to be readable stops being readable. */
          title={submitTooltip}
        >
          <IconPaperPlane size={16} />
          Submit RFQ · {summary.partsCount} {summary.partsCount === 1 ? 'part' : 'parts'}
        </Button>
      )}
      {/* UID-263 (ADR-0087 D1/D4) — the bulk "Add N priced to cart" button,
       * design DOM order: Submit, then this, then Share
       * (`dc.html:4366`->`4371`->`4373`). `showAddBulk` (owned by
       * `RfqReviewPage`, ADR-0087 D1(c)) hides this entirely — not
       * disabled-and-visible — once every line is already cart-eligible (the
       * design routes that case to the Submit CTA instead) or when there is
       * nothing cart-eligible to peel off. */}
      {showAddBulk && (
        <Button
          variant="outline"
          size="lg"
          className="rfq-summary-add-priced-btn"
          disabled={bulkAdding}
          loading={bulkAdding}
          onClick={onAddBulk}
        >
          {bulkAdding ? (
            'Adding to cart…'
          ) : (
            <>
              <IconCartGlyph size={16} />
              Add {cartEligibleCount} priced to cart
            </>
          )}
        </Button>
      )}
      {/* UID-242 Item 1 (ADR-0082) — secondary action, next to Submit. Enabled only once
       * the eagerly-created server draft exists (see the prop doc comment) — but ONLY
       * while this button reads "Share"; see the label-swap note immediately below for
       * why "Generate quote" is deliberately NOT gated the same way.
       *
       * UID-309 — the VISIBLE LABEL is the design-literal "Share"
       * (`Fictiv Platform.dc.html:9773` `shareBtnLabel`; the string
       * "Share for review" appears nowhere in the reference). Verified at Gate 5
       * that this contradicts NO recorded decision: ADR-0082 uses
       * "Share for review" only as the FEATURE NAME in referential position and
       * never specifies button copy — its actual rulings (eager-draft PATCH-then-copy,
       * internal tenant-scoped `/rfq/{id}` deep link, disabled-until-id) all hold
       * verbatim under this label. The feature name is deliberately RETAINED in the
       * ADR prose, code comments and test titles so the ADR-0082 trail stays greppable.
       *
       * UID-466 (ADR-0152) — `onShare` now OPENS the design's share dialog after its
       * PATCH sync rather than copying silently. Nothing here changed: same prop, same
       * `shareEnabled`/`sharing` gating, same label. See `onShare`'s prop doc.
       *
       * UID-401 — IMPLEMENTS the label swap this comment used to defer:
       * the reference also swaps this SAME button to "Generate quote"
       * (`dc.html:9773`'s `shareBtnLabel`) once `quoteView`/`allPriced` is truthy. Driven
       * by the IDENTICAL `showCheckout` signal the Checkout CTA above already uses
       * (`review-domain.allLinesPriced`, owned by `RfqReviewPage`) — never a second,
       * independently-derived predicate, so the two CTAs can't disagree about whether
       * everything is priced. Below the all-priced threshold this button is UNCHANGED:
       * "Share", `onShare`, gated by `shareEnabled`/`sharing` exactly as ADR-0082 ruled.
       * At/above the threshold it becomes "Generate quote", wired to `onGenerateQuote`
       * instead, and is unconditionally enabled — mirroring the sibling Checkout CTA,
       * which is also never gated by `shareEnabled`/`sharing` (those flags describe the
       * COPY-LINK draft-sync precondition, which has nothing to do with either
       * cart-bound CTA). See `onGenerateQuote`'s prop doc comment for what clicking it
       * actually does (`RfqReviewPage.handleGenerateQuoteClick`).
       *
       * UID-359 item 13 (ADR-0118, target row 13) — gains the design's exact
       * Share glyph (`dc.html:4373`, a 3-circle/2-line network icon), matched
       * verbatim by the already-vendored `ShareGlyph`
       * (`components/ui/lucide-glyphs.tsx:188`). Decorative: the visible
       * "Share"/"Generate quote" text already carries the accessible name (pinned
       * exactly in both states — see `RequestSummaryRail.test.tsx`), so the glyph
       * is `aria-hidden` in both states too. */}
      <Button
        variant="outline"
        size="lg"
        disabled={showCheckout ? false : !shareEnabled || sharing}
        loading={showCheckout ? false : sharing}
        onClick={showCheckout ? onGenerateQuote : onShare}
      >
        <ShareGlyph aria-hidden="true" className="rfq-summary-share-icon" />
        {showCheckout ? 'Generate quote' : 'Share'}
      </Button>
    </Card>
  )
}
